// ===================== Weapons =====================
// Each weapon fires on its own cooldown. game.mult(baseCount) applies the
// MULTIPLICATION stat: it returns how many projectiles to actually spawn plus
// a damage multiplier for any overflow beyond the projectile cap.
import { defineWeapon } from '../core/registries.js';
import { CONFIG } from '../config.js';
import { TAU } from '../engine/math.js';
import { Audio } from '../engine/audio.js';

const elColor = (game) => CONFIG.colors[game.player.element] || CONFIG.colors.neutral;

// ---------------------------------------------------------------- Arc Bolt
defineWeapon({
  id: 'bolt', name: 'Arc Bolt', icon: '➤', tags: ['weapon'],
  desc: 'Fires a bolt at the nearest enemy. Multiplication fans it into a volley.',
  maxLevel: 8,
  stats(l) {
    return {
      cooldown: Math.max(0.26, 0.64 - l * 0.03),
      damage: 8 + l * 3.2,
      count: 1 + Math.floor(l / 3),
      speed: 440, pierce: 1 + Math.floor(l / 2),
    };
  },
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    const spread = count > 1 ? 0.20 : 0;
    for (let i = 0; i < count; i++) {
      const a = p.aimAngle + (i - (count - 1) / 2) * spread;
      const sp = s.speed * p.projSpeedMult;
      game.spawnProjectile({
        kind: 'bolt', x: p.x, y: p.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
        r: 7, dmg: game.baseDamage(s.damage) * dmgMult, pierce: s.pierce, life: 1.5,
        color: elColor(game), knockback: 70, weaponId: 'bolt',
      });
    }
    Audio.shoot(p.element);
  },
});

// ---------------------------------------------------------------- Orbit Shards
defineWeapon({
  id: 'orbit', name: 'Orbit Shards', icon: '◍', tags: ['weapon'],
  desc: 'Crystal shards circle you, shredding anything they touch.',
  maxLevel: 8,
  stats(l) {
    return {
      cooldown: 2.0,
      damage: 6 + l * 2.6,
      count: 2 + Math.floor(l / 2),
      orbitRadius: 74 + l * 4,
      orbitSpeed: 3.0 + l * 0.12,
    };
  },
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    for (let i = 0; i < count; i++) {
      const ang = (i / count) * TAU;
      game.spawnProjectile({
        kind: 'orbit', x: p.x, y: p.y, r: 12,
        dmg: game.baseDamage(s.damage) * dmgMult, pierce: -1, rehit: 0.35,
        life: s.cooldown + 0.08, color: elColor(game), knockback: 45,
        orbitAngle: ang, orbitRadius: s.orbitRadius, orbitSpeed: s.orbitSpeed,
        weaponId: 'orbit',
      });
    }
  },
});

// ---------------------------------------------------------------- Nova Burst
defineWeapon({
  id: 'nova', name: 'Nova Burst', icon: '✺', tags: ['weapon'],
  desc: 'Erupts a ring of energy in all directions.',
  maxLevel: 8,
  stats(l) {
    return {
      cooldown: Math.max(1.3, 2.6 - l * 0.12),
      damage: 7 + l * 2.6,
      count: 6 + l,
      speed: 300, pierce: 1,
    };
  },
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    const off = game.rng.next() * TAU;
    for (let i = 0; i < count; i++) {
      const a = off + (i / count) * TAU;
      const sp = s.speed * p.projSpeedMult;
      game.spawnProjectile({
        kind: 'bolt', x: p.x, y: p.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
        r: 6, dmg: game.baseDamage(s.damage) * dmgMult, pierce: s.pierce, life: 1.1,
        color: elColor(game), knockback: 50, weaponId: 'nova',
      });
    }
    Audio.shoot(p.element);
  },
});

// ---------------------------------------------------------------- Spectral Whip
defineWeapon({
  id: 'whip', name: 'Spectral Whip', icon: '〜', tags: ['weapon'],
  desc: 'Sweeps a wide melee arc. Great for clearing crowds up close.',
  maxLevel: 8,
  stats(l) {
    return {
      cooldown: Math.max(0.42, 0.95 - l * 0.05),
      damage: 13 + l * 4.5,
      reach: 92 + l * 7,
      arcHalf: 0.85 + l * 0.02,
      count: 1,
    };
  },
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    for (let i = 0; i < count; i++) {
      const base = p.aimAngle + (i * TAU) / count;
      game.spawnProjectile({
        kind: 'melee', x: p.x, y: p.y, r: s.reach, reach: s.reach, arcHalf: s.arcHalf,
        baseAngle: base - s.arcHalf, sweep: s.arcHalf * 2, life: 0.24,
        dmg: game.baseDamage(s.damage) * dmgMult, pierce: -1, rehit: 0.24,
        color: elColor(game), knockback: 130, weaponId: 'whip',
      });
    }
    Audio.shoot(p.element);
  },
});

// ---------------------------------------------------------------- Homing Spark
defineWeapon({
  id: 'spark', name: 'Homing Spark', icon: '✦', tags: ['weapon'],
  desc: 'Seeking sparks that hunt down the nearest foes.',
  maxLevel: 8,
  stats(l) {
    return {
      cooldown: Math.max(0.55, 1.25 - l * 0.06),
      damage: 9 + l * 3,
      count: 1 + Math.floor(l / 2),
      speed: 250 + l * 8, turn: 5.5, pierce: 0,
    };
  },
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    for (let i = 0; i < count; i++) {
      const a = game.rng.angle();
      const sp = s.speed * p.projSpeedMult;
      game.spawnProjectile({
        kind: 'homing', x: p.x, y: p.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
        r: 7, dmg: game.baseDamage(s.damage) * dmgMult, pierce: s.pierce, life: 2.4,
        speed: sp, turn: s.turn, color: elColor(game), knockback: 40, weaponId: 'spark',
      });
    }
    Audio.shoot(p.element);
  },
});

// ---------------------------------------------------------------- Razor Ring
defineWeapon({
  id: 'boomerang', name: 'Razor Ring', icon: '☯', tags: ['weapon'],
  desc: 'A spinning blade that flies out and boomerangs back through the horde.',
  maxLevel: 8,
  stats(l) {
    return {
      cooldown: Math.max(1.0, 1.7 - l * 0.07),
      damage: 11 + l * 3.6,
      count: 1 + Math.floor(l / 3),
      speed: 330,
    };
  },
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    const spread = count > 1 ? TAU / count : 0;
    for (let i = 0; i < count; i++) {
      const a = p.aimAngle + i * spread;
      const sp = s.speed * p.projSpeedMult;
      game.spawnProjectile({
        kind: 'boomerang', x: p.x, y: p.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
        r: 12, dmg: game.baseDamage(s.damage) * dmgMult, pierce: -1, rehit: 0.4,
        life: 2.2, speed: sp, color: elColor(game), knockback: 90, weaponId: 'boomerang',
      });
    }
    Audio.shoot(p.element);
  },
});

// ---------------------------------------------------------------- Void Mine
defineWeapon({
  id: 'mine', name: 'Void Mine', icon: '◈', tags: ['weapon'],
  desc: 'Drops proximity mines that detonate in a fiery blast.',
  maxLevel: 8,
  stats(l) {
    return {
      cooldown: Math.max(1.4, 2.4 - l * 0.09),
      // Damage curve flattened (was 20 + l*6): mine's blast-hits-everything
      // pattern plus this class-leading base damage made it the best weapon at
      // BOTH horde and single-target DPS. ~23% haircut at L8, count untouched.
      damage: 16 + l * 4.5,
      count: 1 + Math.floor(l / 2),
      area: 82 + l * 7,
    };
  },
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    for (let i = 0; i < count; i++) {
      const a = game.rng.angle();
      const d = game.rng.range(20, 90);
      game.spawnProjectile({
        kind: 'mine', x: p.x + Math.cos(a) * d, y: p.y + Math.sin(a) * d,
        vx: Math.cos(a) * 60, vy: Math.sin(a) * 60,
        r: 11, dmg: game.baseDamage(s.damage) * dmgMult, pierce: -1, rehit: 0,
        life: 8, area: s.area, trigger: 46, color: '#ff8a3d', weaponId: 'mine',
      });
    }
  },
});

// ---------------------------------------------------------------- Warding Circle
defineWeapon({
  id: 'aura', name: 'Warding Circle', icon: '◯', tags: ['weapon'],
  desc: 'A protective ring that constantly sears everything near you.',
  maxLevel: 8,
  stats(l) {
    return {
      cooldown: Math.max(0.32, 0.62 - l * 0.03),
      damage: 6 + l * 2.4,
      area: 88 + l * 11,
      knockback: 46,
    };
  },
  fire(game, s) {
    const p = game.player;
    const r = s.area * p.areaMult;
    // Tick weapons have no projectile count for MULTIPLICATION to multiply, so
    // mirror how mult() scales a 4-projectile fan: the count ratio and any
    // overflow damage fold into one effective damage multiplier.
    const m = game.mult(4);
    const dmg = game.baseDamage(s.damage) * (m.count / 4) * m.dmgMult;
    for (const e of game.enemiesInRadius(p.x, p.y, r)) {
      const dx = e.x - p.x, dy = e.y - p.y;
      const d = Math.hypot(dx, dy) || 1;
      game.dealDamage(e, dmg, { knockback: s.knockback, kx: dx / d, ky: dy / d, applyElement: true, weaponId: 'aura' });
    }
    game.spawnProjectile({
      kind: 'aura', x: p.x, y: p.y, r, baseR: s.area, dmg: 0, pierce: 0, rehit: 0,
      life: s.cooldown * 1.1, color: elColor(game), weaponId: 'aura',
    });
  },
});

// ---------------------------------------------------------------- Hex Pools
defineWeapon({
  id: 'zones', name: 'Hex Pools', icon: '☵', tags: ['weapon'],
  desc: 'Splashes caustic pools that linger, hexing anything that wanders in.',
  maxLevel: 8,
  stats(l) {
    return {
      cooldown: Math.max(1.0, 1.9 - l * 0.08),
      damage: 5 + l * 2.2,
      count: 1 + Math.floor(l / 2),
      area: 58 + l * 6,
      duration: 3.0,
    };
  },
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    for (let i = 0; i < count; i++) {
      const a = game.rng.angle();
      const d = game.rng.range(40, 170);
      game.spawnProjectile({
        kind: 'zone', x: p.x + Math.cos(a) * d, y: p.y + Math.sin(a) * d,
        r: s.area, baseR: s.area, dmg: game.baseDamage(s.damage) * dmgMult,
        pierce: -1, rehit: 0.4, life: s.duration * p.durationMult,
        color: elColor(game), weaponId: 'zones', spin: game.rng.range(0, 6),
      });
    }
  },
});

// ---------------------------------------------------------------- Judgement
defineWeapon({
  id: 'strikes', name: 'Judgement', icon: '↯', tags: ['weapon'],
  desc: 'Bolts fall from above onto random foes, blasting all around them.',
  maxLevel: 8,
  stats(l) {
    return {
      cooldown: Math.max(0.8, 1.7 - l * 0.08),
      damage: 14 + l * 4.5,
      count: 2 + Math.floor(l / 2),
      area: 54 + l * 4,
    };
  },
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    const targets = game.enemiesInRadius(p.x, p.y, 720);
    const col = CONFIG.colors[game.player.element] === CONFIG.colors.neutral ? CONFIG.colors.lightning : elColor(game);
    for (let i = 0; i < count; i++) {
      const e = targets.length ? game.rng.pick(targets) : null;
      const x = e ? e.x : p.x + game.rng.range(-320, 320);
      const y = e ? e.y : p.y + game.rng.range(-320, 320);
      game.spawnProjectile({
        kind: 'aoe', x, y, dmg: game.baseDamage(s.damage) * dmgMult,
        maxR: s.area * p.areaMult, color: col, life: 0.3, pierce: -1, rehit: 0,
        applyElement: true, weaponId: 'strikes',
      });
      game.particles_spark(x, y, col, 6);
    }
    Audio.shoot('lightning');
  },
});
